import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy, ExternalLink, FileText, Link2, MessageCircle, PlugZap, QrCode, RotateCcw, Send, Settings, Ticket, X } from "lucide-react";
import { api, ouvrirPdf } from "./api";
import { Badge, Carte, EnTeteMenu, Puces, Recherche } from "./ui";
import { useConfirmation } from "./Confirmation";
import BenevolesSpectacles, { COURT, EMOJI } from "./BenevolesSpectacles";

/* Spectacles de juin 2027 (école, 30.10.2026) : la billetterie vue par l'école, un menu à lui
   (Jen, 31.10.2026 : « un onglet Spectacle dédié »), sous Cours privés. Revu le 31.10.2026
   au soir (Jen : « Apple style », « je comprends rien », « trop d'espace vide ») :
   · « Vue d'ensemble » : les chiffres clés, puis une carte par représentation — les places
     vendues et les postes de bénévoles en colonnes alignées, avec les icônes du site ;
   · « Bénévoles » : le tableau de service (postes × dates, qui manque), par personne,
     Modifier / Retirer, Écrire, WhatsApp, Imprimer — BenevolesSpectacles.jsx (01.11.2026) ;
   · « Billets & commandes » : par commande le nombre de personnes, payé ou pas, le billet,
     le PDF, Renvoyer, Annuler ; filtres par état et par représentation ;
   · « Entrée (QR code) » : le lien de l'entrée de chaque représentation ;
   · « Réglages de la vente » : une carte par réglage, l'état en clair et un bouton qui dit
     ce qu'il fait (« Ouvrir la vente des billets ») ;
   · le spectacle de Noël a son écran à lui (SpectacleNoel.jsx).
   Grand chantier : visible en test d'abord (le serveur répond 404 en prod). */

const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const MODES = { en_ligne: "en ligne", simule: "en ligne (test)", benevole: "offert (bénévole)", sur_place_cash: "sur place · cash", sur_place_twint: "sur place · TWINT" };
// « Paiement annulé » : annulé ou refusé chez Payrexx, places libérées tout de suite ; payée
// ensuite sans place pour revenir : « Payée · places reprises », à rembourser ou à placer.
const STATUT = (c) => (c.statut === "payee" ? ["Payée", "vert"]
  : c.a_regler ? ["Payée · places reprises", "rouge"]
  : c.statut === "annulee" ? (c.paye_apres_annulation ? ["Annulée · remboursée", "neutre"]
    : c.annulation === "paiement" ? ["Paiement annulé", "neutre"] : ["Annulée", "rouge"])
  : c.abandonnee ? ["Paiement abandonné", "neutre"] : ["Paiement en cours", "bleu"]);
// Les icônes des postes (les mêmes que la page des billets du site) : BenevolesSpectacles.jsx.
const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? "s" : ""}`;
const sansAnnee = (t) => String(t || "").replace(/ 2027/, "");

// `cible` : la ligne visée par le bouton d'un e-mail à l'école (backend mail.lien_console) —
// le n° d'une commande (Billets & commandes) ou un créneau de bénévole (Bénévoles).
export default function Billetterie({ partie = "ensemble", onAller, cible }) {
  const { demander } = useConfirmation();
  const [d, setD] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const charger = useCallback(() => {
    api("/billetterie").then(setD).catch((e) => {
      if (/bientôt|404/.test(String(e.message))) setAbsent(true); else setErreur(e.message);
    });
  }, []);
  useEffect(charger, [charger]);
  // Le spectacle de Noël a sa place à lui (« Spectacle de Noël » en haut du menu, 02.11.2026).
  const agir = async (chemin, methode = "POST", corps = null, succes = "") => {
    setErreur(""); setMessage("");
    try {
      const r = await api(chemin, { method: methode, ...(corps ? { body: JSON.stringify(corps) } : {}) });
      if (r && r.representations) setD(r); else charger();
      if (succes || r?.message) setMessage(succes || r.message);
    } catch (e) { setErreur(e.message); }
  };

  if (absent) return <p className="text-[var(--muted)]">La billetterie des spectacles de juin arrive bientôt.</p>;
  if (!d) return <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  const r = d.reglage;
  const t = d.totaux;

  const tete = (
    <EnTeteMenu ton="annexes" Icon={Ticket} contexte="Spectacles 2026-2027" titre="Spectacles de juin"
      texte="Billets, bénévoles, entrée et vente."
      points={[
        [Ticket, "Héros & Vilains", "Thierrens · 12 et 13 juin"],
        [Ticket, "Le Monde de l'Autre", "Goumoens · 19 et 20 juin"],
      ]}
      droite={<div className="flex flex-wrap gap-2">
        <Badge ton={r.ventes_ouvertes ? "vert" : "neutre"}>{r.ventes_ouvertes ? "Vente ouverte" : "Vente fermée"}</Badge>
        {t.a_confirmer > 0 && <Badge ton="ambre">{t.a_confirmer} bénévole(s) à confirmer</Badge>}
      </div>} testid="billetterie-entete" />
  );
  const bandeaux = (
    <>
      {/* Production : la console est ouverte avant le site (Jen, 03.11.2026 : « on n'ouvre pas
          la billetterie sur le site tant que Payrexx n'est pas réglé »). */}
      {d.site_ouvert === false && (
        <div className="rounded-2xl px-4 py-3 text-sm sans-impression" data-testid="billetterie-site-ferme"
          style={{ background: "#F8FAFC", border: "1px solid var(--border)", color: "var(--muted)" }}>
          <strong style={{ color: "var(--text)" }}>Le site ne vend pas encore de billets.</strong>{" "}
          La page Billets et les inscriptions des bénévoles s'ouvriront sur le site une fois Payrexx réglé. Tout se prépare déjà ici.
        </div>
      )}
      {message && <div className="rounded-2xl px-4 py-3 text-sm sans-impression" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="billetterie-message">{message}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm sans-impression" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>{erreur}</div>}
      {/* Payée après l'annulation de sa commande, sans place pour revenir (01.11.2026). */}
      {t.a_regler > 0 && (
        <div className="rounded-2xl px-4 py-3 text-sm flex flex-wrap items-center gap-3 sans-impression" data-testid="billetterie-a-regler"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
          <span className="flex-1 min-w-[240px]"><strong>{t.a_regler > 1 ? `${t.a_regler} commandes payées` : "Une commande payée"} mais places reprises : à rembourser ou à placer.</strong>{" "}
            La personne a annulé son paiement, puis a payé quand même ; entre-temps, ses places ont été reprises.</span>
          {partie !== "commandes" && onAller && <button className="btn-ghost text-xs" onClick={() => onAller({ onglet: "spectacles-commandes" })}>Voir</button>}
        </div>
      )}
    </>
  );
  const cadre = (testid, contenu) => (
    <div className="space-y-5" data-testid={testid}>
      <div className="sans-impression">{tete}</div>
      {bandeaux}
      {contenu}
    </div>
  );

  if (partie === "reglages") return cadre("console-billetterie-reglages", <Reglages d={d} agir={agir} demander={demander} />);
  // Le tableau de service des bénévoles (01.11.2026) : BenevolesSpectacles.jsx.
  if (partie === "benevoles") return cadre("console-billetterie-benevoles", <BenevolesSpectacles d={d} demander={demander} majDonnees={setD} cible={cible} />);
  if (partie === "commandes") return cadre("console-billetterie-commandes", <Commandes d={d} agir={agir} demander={demander} cible={cible} />);
  if (partie === "entree") return cadre("console-billetterie-entree", <Entree d={d} agir={agir} demander={demander} setMessage={setMessage} />);

  // Vue d'ensemble.
  return cadre("console-billetterie", (
    <>
      {/* Les bénévoles sont confirmés d'office (01.11.2026) : « À confirmer » n'apparaît que
          s'il reste d'anciens créneaux « inscrit ». */}
      <div className={`grid grid-cols-2 md:grid-cols-3 ${t.a_confirmer ? "xl:grid-cols-5" : "xl:grid-cols-4"} gap-3`} data-testid="billetterie-chiffres">
        <Carte titre="Billets vendus" valeur={t.billets} detail={`${pluriel(t.commandes, "commande")} · ${t.personnes} personnes (0-2 ans compris)`}
          onClick={onAller ? () => onAller({ onglet: "spectacles-commandes" }) : undefined} testid="billetterie-chiffre-billets" />
        <Carte titre="Recettes" valeur={`CHF ${Math.round(t.recettes || 0)}`} detail="billets payés, sans frais" ton="primaire" />
        <Carte titre="Bénévoles" valeur={t.benevoles} detail={`${t.creneaux} créneau${t.creneaux > 1 ? "x" : ""} pris`}
          onClick={onAller ? () => onAller({ onglet: "spectacles-benevoles" }) : undefined} testid="billetterie-chiffre-benevoles" />
        <Carte titre="Il manque" valeur={t.manquent} ton={t.manquent ? "ambre" : "vert"} detail={t.manquent ? "créneaux de bénévoles à trouver" : "tous les postes sont pris"} />
        {t.a_confirmer > 0 && <Carte titre="À confirmer" valeur={t.a_confirmer} ton="ambre" detail="anciens créneaux, pas encore confirmés"
          onClick={onAller ? () => onAller({ onglet: "spectacles-benevoles" }) : undefined} />}
      </div>
      <BarreVente r={r} onAller={onAller} />
      {groupes(d.representations).map(([titre, reps]) => (
        <section key={titre} className="space-y-3" data-testid={`billetterie-spectacle-${reps[0].spectacle}`}>
          <h2 className="font-display text-xl tracking-tight">{titre}</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="billetterie-representations">
            {reps.map((x) => <CarteRepresentation key={x.id} x={x} onAller={onAller} />)}
          </div>
        </section>
      ))}
    </>
  ));
}

// Les représentations par spectacle, dans l'ordre des dates.
const groupes = (reps) => Object.entries(reps.reduce((acc, x) => {
  (acc[x.titre] = acc[x.titre] || []).push(x); return acc;
}, {}));
const manque = (x) => x.roles.reduce((n, role) => n + role.restantes, 0);

function BarreVente({ r, onAller }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm rounded-2xl border bg-white px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid="billetterie-etat">
      <Etat ouvert={r.ventes_ouvertes} oui="La vente des billets est ouverte" non="La vente des billets est fermée" />
      <span className="text-[var(--muted)]">·</span>
      <Etat ouvert={r.benevoles_ouverts} oui="les inscriptions des bénévoles sont ouvertes" non="les inscriptions des bénévoles sont fermées" />
      {onAller && <button className="btn-ghost text-xs ml-auto" onClick={() => onAller({ onglet: "spectacles-reglages" })} data-testid="billetterie-vers-reglages">
        <Settings size={12} /> {r.ventes_ouvertes ? "Fermer la vente…" : "Ouvrir la vente…"}</button>}
    </div>
  );
}
const Etat = ({ ouvert, oui, non }) => (
  <span className="inline-flex items-center gap-2">
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: ouvert ? "#22C55E" : "#CBD5E1" }} />
    <span className={ouvert ? "font-medium" : "text-[var(--muted)]"}>{ouvert ? oui : non}</span>
  </span>
);

/* Une représentation : les places (barre), puis les postes de bénévoles en colonnes
   alignées (2 ou 3 selon la largeur), chacun avec son icône et « pris / places ». */
function CarteRepresentation({ x, onAller }) {
  const pourcent = Math.round((x.vendues / x.places) * 100);
  const n = manque(x);
  return (
    <div className="bg-white rounded-2xl border p-4 space-y-3 text-sm" style={{ borderColor: "var(--border)", opacity: x.ouverte ? 1 : 0.6 }} data-testid={`billetterie-rep-${x.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-semibold">{majuscule(x.quand)}</div>
        {!x.ouverte && <Badge ton="neutre">Fermée</Badge>}
      </div>
      <div>
        <div className="flex justify-between text-xs text-[var(--muted)]">
          <span><strong className="text-[var(--text)] text-base">{x.vendues}</strong> / {x.places} places</span>
          <span>{x.restantes} libres{x.entres ? ` · ${x.entres} entrés` : ""}</span>
        </div>
        <div className="h-1.5 rounded-full mt-1.5 overflow-hidden" style={{ background: "#E2E8F0" }}>
          <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, pourcent)}%`, background: pourcent >= 90 ? "#DC2626" : "var(--primary)" }} />
        </div>
      </div>
      <div className="pt-3 border-t" style={{ borderColor: "var(--border-soft)" }}>
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">Bénévoles</span>
          <span className="font-semibold" style={{ color: n ? "#B45309" : "#15803D" }}>{n ? `il en manque ${n}` : "au complet ✓"}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`billetterie-postes-${x.id}`}>
          {x.roles.map((role) => <Poste key={role.role} role={role} onClick={onAller ? () => onAller({ onglet: "spectacles-benevoles" }) : undefined} />)}
        </div>
      </div>
    </div>
  );
}

function Poste({ role, onClick }) {
  const pris = role.places - role.restantes;
  const complet = role.restantes === 0;
  const fond = complet ? "#F0FDF4" : pris ? "#FFFBEB" : "#F8FAFC";
  const bord = complet ? "#BBF7D0" : pris ? "#FDE68A" : "#E2E8F0";
  return (
    <button type="button" onClick={onClick} title={role.description}
      className="rounded-xl border px-2.5 py-2 text-left min-w-0" style={{ background: fond, borderColor: bord }}
      data-testid={`billetterie-poste-${role.role}`}>
      <span className="flex items-center justify-between gap-1">
        <span className="text-lg leading-none" aria-hidden>{EMOJI[role.role]}</span>
        <span className="text-xs font-semibold" style={{ color: complet ? "#15803D" : pris ? "#B45309" : "var(--muted)" }}>{pris}/{role.places}</span>
      </span>
      <span className="block text-[11px] leading-tight mt-1">{COURT[role.role] || role.libelle}</span>
    </button>
  );
}

/* Les commandes : une ligne par représentation commandée, compacte. Le nombre de personnes
   en gros, payé ou pas, puis Billet · PDF · Renvoyer · Annuler. */
const FILTRES = {
  "": () => true,
  payee: (c) => c.statut === "payee",
  en_cours: (c) => c.statut === "en_attente" && !c.abandonnee,
  abandonnee: (c) => c.statut === "en_attente" && c.abandonnee,
  annulee: (c) => c.statut === "annulee" && !c.a_regler,
  a_regler: (c) => c.a_regler,
};
function Commandes({ d, agir, demander, cible }) {
  // Ouvert depuis l'e-mail « À régler » : la commande cherchée par son numéro, dans sa vue.
  const [q, setQ] = useState(cible || "");
  const [etat, setEtat] = useState(() => {
    const visee = cible ? d.commandes.find((c) => c.numero === cible) : null;
    if (visee) return visee.a_regler ? "a_regler" : "";
    return d.commandes.some((c) => c.a_regler) ? "a_regler" : "payee";
  });
  const [rep, setRep] = useState("");
  const tout = d.commandes;
  const nb = (f) => tout.filter(f).length;
  const lignes = useMemo(() => tout.filter(FILTRES[etat])
    .filter((c) => !rep || c.representation_id === rep)
    .filter((c) => !q.trim() || `${c.numero} ${c.prenom} ${c.nom} ${c.email} ${c.telephone}`.toLowerCase().includes(q.trim().toLowerCase())),
  [tout, etat, rep, q]);
  const somme = lignes.reduce((a, c) => ({ personnes: a.personnes + (c.personnes || 0), montant: a.montant + Number(c.montant || 0) }), { personnes: 0, montant: 0 });
  const petit = "btn-ghost text-[11px] !py-1 !px-2";
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Puces valeur={etat} onChange={setEtat} testid="commandes-etat" options={[
          { valeur: "payee", label: "Payées", nb: nb(FILTRES.payee), testid: "commandes-payees" },
          { valeur: "en_cours", label: "Paiement en cours", nb: nb(FILTRES.en_cours) },
          { valeur: "abandonnee", label: "Abandonnées", nb: nb(FILTRES.abandonnee) },
          { valeur: "annulee", label: "Annulées", nb: nb(FILTRES.annulee) },
          ...(nb(FILTRES.a_regler) ? [{ valeur: "a_regler", label: "À régler", nb: nb(FILTRES.a_regler), testid: "commandes-a-regler" }] : []),
          { valeur: "", label: "Toutes", nb: tout.length, testid: "commandes-toutes" }]} />
        <select className="input-field !py-1.5 text-sm" style={{ width: 250 }} value={rep} onChange={(e) => setRep(e.target.value)} data-testid="commandes-representation">
          <option value="">Toutes les représentations</option>
          {d.representations.map((x) => <option key={x.id} value={x.id}>{x.titre} · {sansAnnee(x.quand)}</option>)}
        </select>
        <span className="ml-auto"><Recherche valeur={q} onChange={setQ} placeholder="Nom, e-mail, téléphone ou n°" testid="billetterie-recherche" largeur={260} /></span>
      </div>
      <div className="bg-white rounded-2xl border overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="billetterie-commandes">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
              <th className="px-3 py-2 font-semibold">Commande</th><th className="px-3 py-2 font-semibold">Nom</th>
              <th className="px-3 py-2 font-semibold">Représentation</th><th className="px-3 py-2 font-semibold text-right">Personnes</th>
              <th className="px-3 py-2 font-semibold text-right">Montant</th><th className="px-3 py-2 font-semibold">État</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {lignes.map((c) => {
              const [label, ton] = STATUT(c);
              const detail = [c.plein && `${c.plein} adulte${c.plein > 1 ? "s" : ""}`, c.enfant && `${c.enfant} enfant${c.enfant > 1 ? "s" : ""}`, c.bebe && `${c.bebe} × 0-2 ans`].filter(Boolean).join(" · ");
              return (
                <tr key={c.id} className="border-b last:border-b-0 align-top" style={{ borderColor: "var(--border-soft)" }} data-testid="billetterie-commande">
                  <td className="px-3 py-2 whitespace-nowrap"><strong>{c.numero}</strong><div className="text-[11px] text-[var(--muted)]">{c.commande_le}</div></td>
                  <td className="px-3 py-2">{c.prenom} {c.nom}<div className="text-[11px] text-[var(--muted)]">{c.email}</div>{c.telephone && <div className="text-[11px] text-[var(--muted)] whitespace-nowrap">{c.telephone}</div>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.titre}<div className="text-[11px] text-[var(--muted)]">{sansAnnee(c.quand)}</div></td>
                  <td className="px-3 py-2 text-right"><strong className="text-base" data-testid="commande-personnes">{c.personnes}</strong>
                    <div className="text-[11px] leading-tight text-[var(--muted)] max-w-[8rem] ml-auto">{detail}{c.offerts ? ` · ${c.offerts} offert` : ""}</div></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><strong>{chf(c.montant)}</strong><div className="text-[11px] text-[var(--muted)]">{MODES[c.mode] || c.mode}</div></td>
                  <td className="px-3 py-2 whitespace-nowrap"><Badge ton={ton}>{c.statut === "payee" ? <><Check size={11} /> {label}</> : label}</Badge>
                    {c.statut === "payee" && c.entres > 0 && <div className="text-[11px] text-[var(--muted)] mt-0.5">{c.entres}/{c.personnes} entrés</div>}</td>
                  <td className="px-3 py-2">
                    <span className="flex flex-nowrap justify-end gap-1">
                      {c.statut === "payee" && c.jeton && <a className={petit} href={`${window.location.origin}/billet/${c.jeton}`} target="_blank" rel="noopener noreferrer" title="Le billet avec le QR code"><QrCode size={12} /> Billet</a>}
                      {c.statut === "payee" && <button className={petit} onClick={() => ouvrirPdf(`/billetterie/commandes/${c.id}/pdf`, `Billets ${c.numero}`)} data-testid="commande-pdf"><FileText size={12} /> PDF</button>}
                      {c.statut === "payee" && c.email && <button className={petit} data-testid="commande-renvoyer"
                        onClick={async () => { if (await demander({ titre: `Renvoyer les billets ${c.numero} ?`, texte: `L'e-mail des billets (avec le PDF) repart à ${c.email}.`, valider: "Renvoyer" })) agir(`/billetterie/commandes/${c.id}/renvoyer`); }}><Send size={12} /> Renvoyer</button>}
                      {c.a_regler && <button className={petit} data-testid="commande-placer"
                        onClick={async () => { if (await demander({ titre: `Placer la commande ${c.numero} ?`, texte: `Elle redevient payée pour ${sansAnnee(c.quand)}, s'il reste les places, et les billets partent à ${c.email}.`, valider: "Placer" })) agir(`/billetterie/commandes/${c.id}/placer`); }}><Check size={12} /> Placer</button>}
                      {c.a_regler && <button className={petit} data-testid="commande-remboursee"
                        onClick={async () => { if (await demander({ titre: `${c.numero} remboursée ?`, texte: "À cliquer une fois le remboursement fait dans Payrexx : la commande reste annulée, plus rien à régler.", valider: "C'est remboursé" })) agir(`/billetterie/commandes/${c.id}/remboursee`); }}><RotateCcw size={12} /> Remboursée</button>}
                      {c.statut !== "annulee" && <button className={petit} data-testid="commande-annuler"
                        onClick={async () => { if (await demander({ titre: `Annuler la commande ${c.numero} ?`, texte: "Ses places se libèrent et son billet ne passe plus à l'entrée. Le remboursement éventuel se fait à part (les billets ne sont pas remboursables).", valider: "Annuler la commande", annuler: "Garder la commande" })) agir(`/billetterie/commandes/${c.id}/annuler`); }}><X size={12} /> Annuler</button>}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!lignes.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--muted)]">Aucune commande.</td></tr>}
          </tbody>
          {lignes.length > 1 && (
            <tfoot><tr className="border-t text-xs" style={{ borderColor: "var(--border)" }}>
              <td className="px-3 py-2 text-[var(--muted)]" colSpan={3}>{pluriel(lignes.length, "ligne")}</td>
              <td className="px-3 py-2 text-right font-semibold">{somme.personnes}</td>
              <td className="px-3 py-2 text-right font-semibold">{chf(somme.montant)}</td><td colSpan={2} />
            </tr></tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-[var(--muted)]">
        Une ligne par représentation commandée (un panier de deux spectacles = deux lignes, un seul e-mail et un seul PDF).
        « Paiement annulé » : annulé ou refusé sur la page de paiement, les places sont libérées tout de suite. « Paiement abandonné » : pas payé dans les 30 minutes, les places sont libérées.
        « Annuler » libère les places ; le remboursement éventuel se fait à part. « À régler » : payée après l'annulation, sans place pour revenir — « Placer » dès qu'il y a de la place, ou « Remboursée » une fois remboursée dans Payrexx.
      </p>
    </>
  );
}

function Entree({ d, agir, demander, setMessage }) {
  const copier = async (texte) => { try { await navigator.clipboard.writeText(texte); setMessage("Lien copié."); } catch { setMessage(texte); } };
  return (
    <>
      <div className="rounded-2xl border bg-white p-4 text-sm space-y-2" style={{ borderColor: "var(--border)" }} data-testid="billetterie-qr-explication">
        <div className="font-semibold flex items-center gap-2"><QrCode size={16} /> Le QR code, comment ça marche</div>
        <ol className="list-decimal pl-5 space-y-1 text-[var(--muted)]">
          <li><strong className="text-[var(--text)]">La famille</strong> paie en ligne (TWINT) : elle voit ses billets sur la page « Merci », les reçoit par e-mail
            (PDF en pièce jointe + lien) et dans l'app. <strong className="text-[var(--text)]">Un QR code par commande et par représentation</strong>, valable pour toutes les personnes de la commande.</li>
          <li><strong className="text-[var(--text)]">Avant le spectacle</strong>, crée ci-dessous le « lien de l'entrée », puis « Partager sur WhatsApp » : le message est prêt, choisis le groupe des bénévoles et envoie.</li>
          <li><strong className="text-[var(--text)]">À l'entrée</strong>, le bénévole ouvre ce lien sur son téléphone : « Scanner », il voit le nom et le nombre de personnes,
            puis « Faire entrer » (une famille peut entrer en plusieurs fois : le QR code compte les entrées). Un billet d'une autre date est refusé en rouge.
            Sans QR code : « Chercher » par nom ou numéro. « Vendre » : billets sur place, cash ou TWINT.</li>
        </ol>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {d.representations.filter((x) => x.ouverte).map((x) => (
          <div key={x.id} className="bg-white rounded-2xl border p-4 space-y-3 text-sm" style={{ borderColor: "var(--border)" }} data-testid={`billetterie-entree-${x.id}`}>
            <div><strong>{x.titre}</strong><div className="text-[var(--muted)]">{majuscule(x.quand)}</div></div>
            <div className="flex gap-4 text-xs text-[var(--muted)]">
              <span><strong className="text-[var(--text)]">{x.entres}</strong> entrés sur {x.attendus}</span>
              <span><strong className="text-[var(--text)]">{x.vendues}</strong> / {x.places} places</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link2 size={14} />
              {x.lien_entree ? (
                <>
                  {/* WhatsApp s'ouvre avec le message déjà écrit : il reste à choisir le groupe et
                      envoyer (Jen, 03.11.2026). Aucun site ne peut publier seul dans un groupe WhatsApp. */}
                  <a className="btn-ghost text-xs" style={{ color: "#128C4A", borderColor: "#BBF7D0", background: "#F0FDF4" }}
                    href={`https://wa.me/?text=${encodeURIComponent(`🎟️ Entrée · ${x.titre} · ${majuscule(x.quand)}\nÀ ouvrir sur ton téléphone à l'entrée (Scanner, Chercher, Vendre) :\n${x.lien_entree}`)}`}
                    target="_blank" rel="noopener noreferrer" data-testid={`billetterie-whatsapp-entree-${x.id}`}>
                    <MessageCircle size={12} /> Partager sur WhatsApp
                  </a>
                  <button className="btn-ghost text-xs" onClick={() => copier(x.lien_entree)} data-testid={`billetterie-copier-${x.id}`}><Copy size={12} /> Copier le lien</button>
                  <a className="btn-ghost text-xs" href={x.lien_entree} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> Ouvrir</a>
                  <button className="btn-ghost text-xs" onClick={async () => { if (await demander({ titre: "Nouveau lien de l'entrée ?", texte: "L'ancien lien ne marchera plus : envoie le nouveau aux bénévoles de l'entrée.", valider: "Nouveau lien" })) agir(`/billetterie/entree/${x.id}/nouveau-lien`); }}><RotateCcw size={12} /> Nouveau</button>
                </>
              ) : <button className="btn-primary text-xs" onClick={() => agir(`/billetterie/entree/${x.id}/nouveau-lien`)} data-testid={`billetterie-lien-${x.id}`}>Créer le lien de l'entrée</button>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* Réglages : une carte par réglage. L'état en clair (« Fermée : personne ne peut acheter »)
   et un bouton qui dit ce qu'il fait (Jen, 31.10.2026 : « comprendre que si on appuie, ça
   ouvre la vente »). */
function Reglages({ d, agir, demander }) {
  const [test, setTest] = useState(null);
  const r = d.reglage;
  const regler = (champs) => agir("/billetterie/reglage", "PUT", champs);
  const tester = async () => { setTest(null); try { setTest(await api("/billetterie/payrexx/tester", { method: "POST" })); } catch (e) { setTest({ ok: false, message: e.message }); } };
  const samedi = d.representations.find((x) => x.sur_demande);
  return (
    <div className="space-y-3 max-w-3xl" data-testid="billetterie-reglages">
      <Interrupteur testid="billetterie-ventes" titre="Vente des billets" ouvert={r.ventes_ouvertes}
        oui="Les familles achètent leurs billets sur le site (TWINT ou carte)."
        non="Personne ne peut acheter de billets pour l'instant."
        ouvrir="Ouvrir la vente des billets" fermer="Fermer la vente"
        onClick={async () => {
          const ok = await demander(r.ventes_ouvertes
            ? { titre: "Fermer la vente des billets ?", texte: "Plus personne ne pourra acheter de billets sur le site. Les billets déjà vendus restent valables.", valider: "Fermer la vente" }
            : { titre: "Ouvrir la vente des billets ?", texte: "Les billets se vendent tout de suite sur la page Billets du site.", valider: "Ouvrir la vente" });
          if (ok) regler({ ventes_ouvertes: !r.ventes_ouvertes });
        }} />
      <Interrupteur testid="billetterie-benevoles" titre="Inscriptions des bénévoles" ouvert={r.benevoles_ouverts}
        oui="Les familles s'inscrivent comme bénévoles sur le site."
        non="Le formulaire des bénévoles n'est pas proposé."
        ouvrir="Ouvrir les inscriptions" fermer="Fermer les inscriptions"
        onClick={() => regler({ benevoles_ouverts: !r.benevoles_ouverts })} />
      {(d.groupes_whatsapp || []).length > 0 && <GroupesWhatsApp groupes={d.groupes_whatsapp} regler={regler} />}
      {samedi && <Interrupteur testid="billetterie-samedi" titre={`${samedi.titre} · ${majuscule(sansAnnee(samedi.quand))}`} ouvert={samedi.ouverte}
        oui="Cette représentation est en vente."
        non="À ouvrir seulement s'il y a beaucoup de demande."
        ouvrir="Ouvrir cette représentation" fermer="Fermer cette représentation"
        onClick={() => regler({ representations_ouvertes: { [samedi.id]: !samedi.ouverte } })} />}
      <div className="bg-white rounded-2xl border p-4 flex flex-wrap items-center gap-3 text-sm" style={{ borderColor: "var(--border)" }}>
        <PlugZap size={16} />
        <span className="flex-1 min-w-[14rem]">Payrexx : {d.payrexx.pret ? <>instance <strong>{d.payrexx.instance}</strong> · clé lue dans <code>{d.payrexx.variable_cle}</code></> : "pas encore configuré"}</span>
        <button className="btn-ghost text-xs" onClick={tester} data-testid="billetterie-tester">Tester la connexion</button>
        {test && <span className="w-full" style={{ color: test.ok ? "#15803D" : "#B91C1C" }} data-testid="billetterie-test">{test.message}</span>}
      </div>
      {/* Version de test seulement (Jen, 01.11.2026 : « tester l'achat de bout en bout sans
          payer ») : simulé d'office ; « Payrexx » pour essayer la vraie page de paiement. */}
      {d.environnement_test && (
        // L'état suit le choix de l'école (01.11.2026 : sans Payrexx configuré, « Passer au vrai
        // Payrexx » semblait ne rien faire — le paiement reste simulé, et c'est dit).
        <Interrupteur testid="billetterie-paiement-test" titre="Paiement · version de test" ouvert={r.paiement_test !== "payrexx"}
          oui="Simulé : « Payer » mène à « Simuler le paiement », rien n'est débité. Billets, e-mail avec le PDF, app et entrée marchent comme en vrai."
          non={d.payrexx.pret ? "Vrai Payrexx : le paiement est réellement débité (à rembourser ensuite)."
            : "Vrai Payrexx choisi, mais Payrexx n'est pas encore configuré (voir ci-dessus) : en attendant, le paiement reste simulé."}
          ouvrir="Simuler le paiement" fermer="Passer au vrai Payrexx" etats={["Simulé", "Payrexx"]}
          onClick={() => regler({ paiement_test: r.paiement_test === "payrexx" ? "simule" : "payrexx" })} />
      )}
      <p className="text-xs text-[var(--muted)]">La vente et les bénévoles restent fermés tant que tu ne les ouvres pas (la communauté d'abord, puis la pub à l'extérieur). Billets ni remboursables ni échangeables : c'est écrit sur la page et sur la confirmation.</p>
    </div>
  );
}

/* Le groupe WhatsApp des bénévoles de chaque spectacle (Jen, 03.11.2026 : l'école met le
   lien elle-même) : il part dans l'e-mail de confirmation, la page du bénévole et le bouton
   « Groupes WhatsApp » de Bénévoles. Vide = pas de groupe. */
function GroupesWhatsApp({ groupes, regler }) {
  const [liens, setLiens] = useState({});
  useEffect(() => { setLiens(Object.fromEntries(groupes.map((g) => [g.id, g.lien || ""]))); }, [groupes]);
  return (
    <div className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }} data-testid="billetterie-whatsapp-carte">
      <div className="flex items-center gap-2 font-semibold">
        <MessageCircle size={16} style={{ color: "#25D366" }} /> Groupes WhatsApp des bénévoles
      </div>
      <p className="text-sm text-[var(--muted)] mt-0.5">
        Le lien part dans l'e-mail de chaque bénévole. Dans WhatsApp : le groupe → « Inviter via un lien » → « Copier le lien ».
      </p>
      <div className="mt-3 space-y-2">
        {groupes.map((g) => {
          const change = (liens[g.id] || "").trim() !== (g.lien || "");
          return (
            <div key={g.id} className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium w-44 shrink-0">{g.titre}</span>
              <input value={liens[g.id] || ""} placeholder="https://chat.whatsapp.com/…" inputMode="url"
                onChange={(e) => setLiens({ ...liens, [g.id]: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && change) regler({ whatsapp: { [g.id]: liens[g.id] || "" } }); }}
                className="flex-1 min-w-[14rem] rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--border)" }}
                data-testid={`billetterie-whatsapp-${g.id}`} />
              {change ? (
                <button className="btn-primary text-sm" onClick={() => regler({ whatsapp: { [g.id]: liens[g.id] || "" } })}
                  data-testid={`billetterie-whatsapp-ok-${g.id}`}>Enregistrer</button>
              ) : g.lien ? (
                <a className="btn-ghost text-sm inline-flex items-center gap-1" href={g.lien} target="_blank" rel="noopener noreferrer">
                  Ouvrir <ExternalLink size={12} /></a>
              ) : <span className="text-xs text-[var(--muted)]">pas de groupe</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Interrupteur = ({ titre, ouvert, oui, non, ouvrir, fermer, onClick, testid, etats = ["Ouvert", "Fermé"] }) => (
  <div className="bg-white rounded-2xl border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: ouvert ? "#86EFAC" : "var(--border)" }} data-testid={`${testid}-carte`}>
    <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: ouvert ? "#DCFCE7" : "#F1F5F9" }}>
      <span className="w-3 h-3 rounded-full" style={{ background: ouvert ? "#22C55E" : "#94A3B8" }} />
    </span>
    <div className="flex-1 min-w-[14rem]">
      <div className="font-semibold">{titre} <span className="text-[10px] font-bold uppercase tracking-[0.12em] ml-1.5 align-middle" style={{ color: ouvert ? "#15803D" : "#64748B" }}>{ouvert ? etats[0] : etats[1]}</span></div>
      <div className="text-sm text-[var(--muted)]">{ouvert ? oui : non}</div>
    </div>
    <button onClick={onClick} data-testid={testid} className={ouvert ? "btn-ghost text-sm" : "btn-primary text-sm"}>{ouvert ? fermer : ouvrir}</button>
  </div>
);
